import React, { useState, useCallback } from 'react';
import ConfirmEmailModal from '../common/modals/confirm-modal-action';
import ErrorModal from '../common/modals/error-modal';
import { useNavigate } from 'react-router-dom';
import { handleError } from '../../../core/utils/errorUtils';
import { ValidationError } from '../../../core/form/validationForm';
import { useLocation } from 'react-router-dom';
import { all_routes } from '../../../core/data/routes/all_routes';
import SuccessEmailModal from '../common/modals/success-buy-modal-admin';
import { apiRepository } from '../../../api/apiRepository';
import { PaymentSummaryProps } from './types/steps';

import NavigationButton from './components/NavigationButton';
import ApplyCoupon from './components/ApplyCoupon';
import CheckoutSummary from './components/CheckoutSummary';
import ProductList from './components/ProductList';
import OrderDetails from './components/OrderDetails';
import { convertStringToLocalDate, getCurrentLocalDate } from '../../../core/utils/dateUtils';

import { calculateNewDiscount, initializeProducts } from './utils';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const PaymentSummary: React.FC<PaymentSummaryProps> = ({ prevTab, selectedProducts, summaryData }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const query = useQuery();
  const navigate = useNavigate();
  const routes = all_routes
  const [totalDiscountCoupon, setTotalDiscountCoupon] = useState<any>({});
  const [info, setInfo] = useState<any[]>([]);
  const [loadingCoupon, setLoadingCoupon] = useState<boolean>(false);
  const [products, setProducts] = useState(() => initializeProducts(selectedProducts));

  const calculateTotalAmount = () => {
    const total = products.reduce((total: number, product: any) =>
      total + (product.price * product.quantity - (product.amountDiscount || 0)), 0);
    return total - (totalDiscountCoupon.discount || 0);
  };

  const sendMail = async () => {
    setInfo([]);
    const totalAmount = calculateTotalAmount();
    const totalDiscount = totalDiscountCoupon.discount || 0;
    const discountPerProduct = totalDiscountCoupon.discount / products.length;
    const paymentDetails = products.map((product: any) => {
      return {
        packetId: product.id,
        quantity: product.quantity,
        amount: (product.price * product.quantity) - (Number(product.amountDiscount || 0) + Number(discountPerProduct || 0)),
        amountDiscount: product.amountDiscount || 0,
        coupon: product.coupon || '' // Include individual coupon code if applicable
      };
    });

    const totalAmountDiscount = products.reduce((total: number, product: any) => total + (product.amountDiscount || 0), 0) + Number(totalDiscount);

    const paymentData = {
      userId: summaryData.user.value,
      jsonResponse: 'LIFT WEB',
      status: 'COMPLETED',
      typeDiscount: totalAmountDiscount ? ( totalDiscountCoupon.typeDiscount ? 'PORCENTAJE' : 'MONTO' ): "",
      amount: totalAmount,
      amountDiscount: totalAmountDiscount,
      paymentDetails,
      codesCoupons: totalDiscountCoupon.code ? [ totalDiscountCoupon.code ] : [],
      typePayment: summaryData.paymentMethods,
      paymentMpId: summaryData.paymentMethods === 'MERCADO PAGO' ? summaryData.operationNumber : undefined,
      operationNumber: Number(summaryData.operationNumber) || '',
    };

    try {
      setLoading(true);

      await apiRepository.createPayments(paymentData);
      setShowConfirmModal(false);
      setShowSuccessModal(true);
    } catch (error: any) {
      setErrorMessage(error.response.data.message || 'Error al guardar el pago. Por favor, inténtelo de nuevo.');
      setShowConfirmModal(false);
      setShowSuccessModal(false);
      setShowErrorModal(true);
      handleError(error, setErrors, "confirm-email");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmModalClose = () => {
    setShowConfirmModal(false);
  };

  const handleSuccessConfirm = () => {
    setShowSuccessModal(false);
    if (query.get('retry') === 'true') {
      navigate(routes.salesHistory);
    } else {
      setProducts(selectedProducts.map(product => ({
        ...product,
        quantity: 1,
        amountDiscount: 0,
        coupon: ''
      })));
      setTotalDiscountCoupon({});
      setInfo([]);
      navigate(routes.addPayment, { state: { reset: true } });
    }
  };

  const handleErrorModalClose = () => {
    setShowErrorModal(false);
  };

  const validateCoupon = async (couponCode: string) => {
    setLoadingCoupon(true);
    try {
      const response = await apiRepository.validateCouponCode(couponCode);
      const couponData = response.data.data;
      const { typeGeneralProduct, typeDiscount, amount, code, packetId, packageTypeId, dateExpired } = couponData;


      // Validación de expiración
      if (convertStringToLocalDate(dateExpired) < getCurrentLocalDate()) {
        setInfo([{ field: 'error', message: "Este cupón ya ha expirado" }]);
        return;
      }

      // Cupón general (total)
      if (typeGeneralProduct === 0) {
        if (totalDiscountCoupon.id) {
          setInfo([{ field: 'error', message: 'Ya se ha aplicado un cupón general.' }]);
          return;
        }

        const discount = typeDiscount
          ? calculateTotalAmount() * (amount / 100)
          : amount;

        setTotalDiscountCoupon({ ...couponData, discount });
        setInfo([{ field: 'success', message: 'Cupón aplicado correctamente' }]);
        return true;
      }

      // Cupón por producto
      if (typeGeneralProduct === 1) {
        const couponProduct = products.find((product: any) => product.id === packetId);

        if (!couponProduct) {
          setInfo([{ field: 'error', message: 'El producto especificado en el cupón no está en los productos seleccionados.' }]);
          return;
        }

        if (couponProduct.amountDiscount > 0.0) {
          setInfo([{ field: 'error', message: 'Ya se ha aplicado un descuento a este producto.' }]);
          return;
        }

        setProducts(products.map((product: any) => {
          if (product.id === packetId) {
            const discount = typeDiscount
              ? (product.price * (amount / 100)) * product.quantity
              : amount;

            return { ...product, coupon: code, amountDiscount: discount, typeDiscount: typeDiscount };
          }
          return product;
        }));

        setInfo([{ field: 'success', message: 'Cupón aplicado correctamente' }]);
        return true;
      }

      // Cupón por categoría
      if (typeGeneralProduct === 2) {
        const matchingProducts = products.filter((product: any) => product.packageTypeId === packageTypeId);

        if (matchingProducts.length === 0) {
          setInfo([{ field: 'error', message: 'Ningún producto coincide con la categoría del cupón.' }]);
          return;
        }

        const hasCategoryCoupon = matchingProducts.some(
          (product: any) => product.amountDiscount > 0
        );

        if (hasCategoryCoupon) {
          setInfo([{ field: 'error', message: 'Ya se ha aplicado un cupón a esta categoría. Elimina el cupón anterior para aplicar uno nuevo.' }]);
          return;
        }

        const updatedProducts = products.map((product: any) => {
          if (product.packageTypeId === packageTypeId) {
            if (product.amountDiscount > 0.0) {
              return product; // no sobrescribimos descuentos existentes
            }

            const discount = typeDiscount
              ? (product.price * (amount / 100)) * product.quantity
              : amount;

            return { ...product, coupon: code, amountDiscount: discount, typeDiscount: typeDiscount  };
          }
          return product;
        });

        setProducts(updatedProducts);
        setInfo([{ field: 'success', message: 'Cupón aplicado correctamente' }]);
        return true;
      }

      setInfo([{ field: 'error', message: 'Tipo de cupón no reconocido.' }]);
    } catch (error) {
      setInfo([{ field: 'error', message: 'Error al aplicar el cupón' }]);
    } finally {
      setLoadingCoupon(false);
    }
  };

  const removeGeneralCoupon = () => {
    setTotalDiscountCoupon({});
    setInfo([{ field: 'success', message: 'Cupón general eliminado correctamente' }]);
  };

  const removeProductCoupon = (couponCode: string) => {
    setProducts(products.map((product: any) => {
      if (product.coupon === couponCode) {
        return {
          ...product,
          coupon: '',
          amountDiscount: 0
        };
      }
      return product;
    }));
    setInfo([{ field: 'success', message: `Cupón ${couponCode} eliminado correctamente` }]);
  };

  const applyManualDiscount = (discountValues: any) => {
    const value = Number(discountValues.value);

    if (isNaN(value) || value <= 0) {
      setInfo([{ field: 'error', message: 'El valor debe ser un número mayor que 0' }]);
      return false;
    }

    let discount = 0;

    if (totalDiscountCoupon.amount > 0.0) {
      setInfo([{ field: 'error', message: 'Ya se ha aplicado un descuento general.' }]);
      return false;
    }

    if (discountValues.type) {
      discount = calculateTotalAmount() * (discountValues.value / 100);
    } else {
      discount = discountValues.value;
    }

    const couponData = {
      code: '',
      typeDiscount: discountValues.type,
      amount: Number(discount),
      typeGeneralProduct: false,
    };

    setTotalDiscountCoupon({ ...couponData, discount });
    setInfo([{ field: 'success', message: 'Cupón aplicado correctamente' }]);
  }

  const removeAllCoupons = () => {
    setTotalDiscountCoupon({});
    setProducts(products.map((product: any) => {
      product.amountDiscount = 0;
      product.coupon = '';
      return product;
    }));
    setInfo([]);
  };

  const updateQuantity = useCallback((productId: number, operation: 'increase' | 'decrease') => {
    setProducts((prevProducts: any) => prevProducts.map((product: any) => {
      if (product.id === productId) {
        const newQuantity = operation === 'increase'
          ? product.quantity + 1
          : Math.max(1, product.quantity - 1);

        if (newQuantity !== product.quantity) {
          return {
            ...product,
            quantity: newQuantity,
            amountDiscount: calculateNewDiscount(product.amountDiscount, product.quantity, newQuantity, product.typeDiscount)
          };
        }
      }
      return product;
    }));
  }, []);

  const increaseQuantity = useCallback((productId: number) => {
    updateQuantity(productId, 'increase');
  }, [updateQuantity]);

  const decreaseQuantity = useCallback((productId: number) => {
    updateQuantity(productId, 'decrease');
  }, [updateQuantity]);

  return (
    <>
      <div className="addition-service card-section space-service">
        <div className="row">
          <div className="col-md-12">
            <div className="heading-service">
              <h4 className="text-secondary mb-2">Resumen de la Compra</h4>
            </div>
          </div>

          <div className="col-md-7 col-xxl-8">
            <OrderDetails
              summaryData={summaryData}
            />
          </div>

          <div className="col-md-5 col-xxl-4">
            <ProductList
              products={products}
              decreaseQuantity={decreaseQuantity}
              increaseQuantity={increaseQuantity}
            />

            <CheckoutSummary
              totalDiscountCoupon={totalDiscountCoupon}
              total={calculateTotalAmount()}
              products={products}
              onRemoveGeneralCoupon={removeGeneralCoupon}
              onRemoveProductCoupon={removeProductCoupon}
            />

            <ApplyCoupon
              applyManualDiscount={applyManualDiscount}
              removeAllCoupons={removeAllCoupons}
              loadingValidation={loadingCoupon}
              validateCoupon={validateCoupon}
              info={info}
            />
          </div>
        </div>
      </div>

      <NavigationButton
        onConfirm={handleConfirm}
        loading={loading}
        prevTab={prevTab}
      />

      <ConfirmEmailModal
        onCancel={handleConfirmModalClose}
        show={showConfirmModal}
        onConfirm={sendMail}
        loading={loading}
        error={[]}
        message="¿Estás seguro de que deseas confirmar la compra?"
      />
      <SuccessEmailModal show={showSuccessModal} onClose={() => setShowSuccessModal(false)} onConfirm={handleSuccessConfirm} />
      <ErrorModal show={showErrorModal} onClose={handleErrorModalClose} message={errorMessage} />
    </>
  );
};

export default PaymentSummary;